import { isLoginApiResult } from "api/helpers/typeGuards";

import { axiosAuthInstance } from "../api/axiosAuthInstance";
import { readHandoff, writeHandoff } from "./refreshHandoff";
import { prepareTokenData } from "./utils";

import type { ObjectValuesAsType } from "utils/types";

import type { SerializedAuthUser } from "./utils";

export const REFRESH_RESULT = {
  refreshed: "refreshed",
  adopted: "adopted",
  valid: "valid",
  failed: "failed",
  noToken: "noToken",
} as const;

export type RefreshResult = ObjectValuesAsType<typeof REFRESH_RESULT>;

export interface AcquireRefreshResult {
  status: RefreshResult;
  tokenData?: SerializedAuthUser;
}

const REFRESH_LOCK_NAME = "volt-token-refresh";
const TOKEN_REFRESH_URL = "/token/refresh";

// `axiosAuthInstance` is pinned to the production API base (FUZEBOX_API_URL).
// The refresh token is environment-insensitive, so refresh always targets
// production regardless of the active sandbox/production toggle.
async function postTokenRefresh(refreshToken: string): Promise<unknown> {
  const response = await axiosAuthInstance.post(TOKEN_REFRESH_URL, {
    refresh_token: refreshToken,
  });

  return response.data;
}

/**
 * Consumes the single-use refresh token under a cross-tab lock so that exactly
 * one tab ever POSTs `/token/refresh` for a given token generation.
 *
 * Order inside the lock:
 *  1. Adopt a fresh handoff token if another tab just refreshed (no POST).
 *  2. If the access token is already valid, do nothing.
 *  3. Otherwise POST the refresh; on success persist the handoff (synchronously,
 *     before the lock releases) and return the new token data.
 *
 * The getters are read at lock-acquire time so the freshness checks see the
 * caller's current token. Callers dispatch `setAccessTokenData(tokenData)` when
 * `tokenData` is returned and decide about logout based on `status`.
 */
export async function acquireRefresh(
  getRefreshToken: () => string | null,
  isAccessTokenValid: () => boolean
): Promise<AcquireRefreshResult> {
  const runExclusive = async (): Promise<AcquireRefreshResult> => {
    const handoff = readHandoff();

    if (
      handoff &&
      handoff.expirationTimestamp > Date.now() &&
      handoff.refreshToken !== getRefreshToken()
    ) {
      return { status: REFRESH_RESULT.adopted, tokenData: handoff };
    }

    if (isAccessTokenValid()) {
      return { status: REFRESH_RESULT.valid };
    }

    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      return { status: REFRESH_RESULT.noToken };
    }

    try {
      const result = await postTokenRefresh(refreshToken);

      if (!isLoginApiResult(result)) {
        return { status: REFRESH_RESULT.failed };
      }

      const tokenData = prepareTokenData(result);

      writeHandoff(tokenData);

      return { status: REFRESH_RESULT.refreshed, tokenData };
    } catch {
      return { status: REFRESH_RESULT.failed };
    }
  };

  if (navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, runExclusive);
  }

  return runExclusive();
}
